import React from 'react';
import styled from '@emotion/styled';
import type { BorderColorProps, BorderStyleProps } from 'styled-system';
import { borderColor, borderStyle } from 'styled-system';

import Separator from 'common/components/Separator';
import type { BorderTopWidthProps } from 'common/theme/system-utilities';
import { borderTopWidth } from 'common/theme/system-utilities';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import { NavigationItem, NavigationItemBody } from './NavigationListItem';
import PromoImage from './PromoImage';
import type { NavigationListProps } from './types';

interface NavigationListItemsProps extends NavigationListProps {
  items: GraphqlNavigationItemProps[];
  groupId: number;
  withPromoImage: boolean;
}

const StyledSeparator = styled(Separator)<
  BorderStyleProps & BorderTopWidthProps & BorderColorProps
>`
  ${borderStyle};
  ${borderColor};
  ${borderTopWidth};
`;

const styledSeparatorStyles = {
  my: 'sp_16',
  mx: 'sp_8',
  borderTopWidth: 0,
  borderStyle: 'solid',
  borderColor: 'sys.neutral.border.weak',
};

const getDividerIndex = (items: GraphqlNavigationItemProps[]) => {
  // No primary means all of them are secondary
  if (!items.find(item => item.isPrimary)) {
    return -1;
  }

  const firstNonPrimary = items.findIndex(item => !item.isPrimary);

  // Because of "All Category" item, ignore if index is 1
  if (firstNonPrimary === 1) {
    return -1;
  }

  return firstNonPrimary;
};

export const NavigationListItems = ({
  items,
  withPromoImage,
  ...props
}: NavigationListItemsProps) => {
  const dividerIndex = getDividerIndex(items);

  return (
    <>
      {items.map(({ promoImgV2, ...item }, id) => {
        const dataTestId = `navigation-item-${id}`;
        if (promoImgV2) {
          return withPromoImage ? (
            <NavigationItem key={id} data-testid={dataTestId} flexDirection="column">
              <StyledSeparator {...styledSeparatorStyles} />
              <PromoImage {...promoImgV2} />
            </NavigationItem>
          ) : null;
        }

        const withDivider = dividerIndex === id;

        return (
          <NavigationItem
            key={id}
            data-testid={dataTestId}
            {...(withDivider ? { flexDirection: 'column' } : {})}
          >
            {withDivider && <StyledSeparator {...styledSeparatorStyles} />}
            <NavigationItemBody {...props} id={id} item={item} listLength={items.length} />
          </NavigationItem>
        );
      })}
    </>
  );
};
